import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useFormik } from "formik";
import * as yup from "yup";

import { styled } from "@mui/material/styles";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";

import TextFieldTheme from "../../../shared/general/TextFieldTheme";
import DrawerCustom from "../../../shared/general/Drawer";
import ButtonBlue from "../../../shared/general/ButtonBlue";

import { addAddition } from "../../../../../actions/paytypes";

const StyledContent = styled("div")({
  width: 550,
  padding: 24,
  "& .text-code": {
    marginBottom: 16,
    width: "100%",
    "& .MuiOutlinedInput-input": {
      paddingLeft: "0 !important",
    },
  },
  "& .text-name": {
    marginBottom: 16,
    width: "100%",
  },
  "& .radio": {
    marginBottom: 8,
    width: "100%",
  },
  "& .MuiFormControl-root": {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
});

const StyledFooter = styled("div")({
  padding: 16,
  display: "flex",
  justifyContent: "flex-end",
  "& .cancel": {
    marginRight: 8,
  },
});

const DialogNew = (props) => {
  const { open, selectedValue, mode, handleClose, handleSubmit } = props;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const validationSchema = yup.object({
    payTypes: yup.string().required(t("ThisFieldIsRequired")),
    name: yup.string().required(t("ThisFieldIsRequired")),
    code: yup
      .string()
      .min(4, t("CodeLength46Char"))
      .max(6, t("CodeLength46Char"))
      .required(t("ThisFieldIsRequired")),
    isTaxable: yup.string().required(t("PleaseSelectInformation")),
    isRecurring: yup.string().required(t("PleaseSelectInformation")),
    isPercentage: yup.string().required(t("PleaseSelectInformation")),
    isSso: yup.string().required(t("PleaseSelectInformation")),
    isPf: yup.string().required(t("PleaseSelectInformation")),
    isEWF: yup.string().required(t("PleaseSelectInformation")),
    mode: yup.string().required(t("PleaseSelectMode")),
    amount: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? 0 : value
      )
      .when("mode", {
        is: "2",
        then: (schema) => schema.min(0, t("MinimumValueIs0")),
        otherwise: (schema) => schema,
      }),
    percent: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? 0 : value
      )
      .when("mode", {
        is: "3",
        then: (schema) =>
          schema.min(0, t("MinimumValueIs0")).max(100, t("MaximumValueIs100")),
        otherwise: (schema) => schema,
      }),
  });

  const formik = useFormik({
    initialValues: {
      payTypes: "addition",
      preCode: "AR-",
      code: "",
      name: "",
      isTaxable: "",
      isRecurring: "1",
      isPercentage: "",
      isSso: "",
      isPf: "",
      isEWF: "",
      mode: "1",
      amount: "",
      percent: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  useEffect(() => {
    if (open && mode === "new") {
      formik.resetForm();
    }
  }, [open, mode]);

  useEffect(() => {
    renderTextCode();
  }, [formik.values]);

  useEffect(() => {
    if (open && mode === "edit" && selectedValue) {
      formik.resetForm();
      formik.setFieldValue(
        "payTypes",
        selectedValue.hasOwnProperty("idAddition") ? "addition" : "deduction"
      );
      formik.setFieldValue("preCode", selectedValue.code.split("-")[0]);
      formik.setFieldValue("code", selectedValue.code.split("-")[1]);
      formik.setFieldValue("name", selectedValue.name);
      formik.setFieldValue("isRecurring", selectedValue.isRecurring);
      formik.setFieldValue("isTaxable", selectedValue.isTaxable);
      formik.setFieldValue("isPercentage", selectedValue.isPercentage);
      formik.setFieldValue("isSso", selectedValue.isSso);
      formik.setFieldValue("isPf", selectedValue.isPf);
      formik.setFieldValue("isEWF", selectedValue.isEWF || 0);
      formik.setFieldValue("mode", selectedValue.mode);
      formik.setFieldValue("amount", selectedValue.amount);
      formik.setFieldValue("percent", selectedValue.percent);
    }
  }, [selectedValue, open, mode]);

  const renderTextCode = () => {
    if (formik.values.payTypes === "addition") {
      if (parseInt(formik.values.isRecurring) === 1) {
        formik.setFieldValue("preCode", "AR-");
      } else {
        formik.setFieldValue("preCode", "AO-");
      }
    } else {
      if (parseInt(formik.values.isRecurring) === 1) {
        formik.setFieldValue("preCode", "DR-");
      } else {
        formik.setFieldValue("preCode", "DO-");
      }
    }
  };

  useEffect(() => {
    if (formik.values.mode === "2") {
      formik.setFieldValue("percent", 0);
    } else if (formik.values.mode === "3") {
      formik.setFieldValue("amount", 0);
    }
  }, [formik.values.mode]);

  
  return (
    <DrawerCustom
      open={open}
      onClose={handleClose}
      title={`${t("CreateADDE")}`}
      anchor={"right"}
    >
      <StyledContent>
        <form onSubmit={formik.handleSubmit}>
          <FormControl
            component="fieldset"
            className="radio"
            disabled={mode === "edit"}
            error={formik.touched.payTypes && Boolean(formik.errors.payTypes)}
          >
            <FormLabel component="legend">{t("ItemType")}</FormLabel>
            <RadioGroup
              row
              aria-label="paytypes"
              onChange={formik.handleChange}
              name="payTypes"
              value={formik.values.payTypes}
            >
              <FormControlLabel
                value="addition"
                control={<Radio />}
                label={`${t("Addition")}`}
              />
              <FormControlLabel
                value="deduction"
                control={<Radio />}
                label={`${t("Deduction")}`}
              />
            </RadioGroup>
            {Boolean(formik.errors.payTypes) && (
              <FormHelperText>{formik.errors.payTypes}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            component="fieldset"
            className="radio"
            disabled={mode === "edit"}
            error={
              formik.touched.isRecurring && Boolean(formik.errors.isRecurring)
            }
          >
            <FormLabel component="legend">
              {`${t("Recurring")} ${t("orNot")}`}
            </FormLabel>
            <RadioGroup
              row
              aria-label="isRecurring"
              onChange={formik.handleChange}
              name="isRecurring"
              value={formik.values.isRecurring}
            >
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={t("Yes")}
              />
              <FormControlLabel value={0} control={<Radio />} label={t("No")} />
            </RadioGroup>
            {formik.touched.isRecurring &&
              Boolean(formik.errors.isRecurring) && (
                <FormHelperText>{formik.errors.isRecurring}</FormHelperText>
              )}
          </FormControl>
          <TextFieldTheme
            className="text-code"
            label={`${t("Code")}`}
            variant={"outlined"}
            name="code"
            disabled={mode === "edit"}
            value={formik.values.code}
            onChange={formik.handleChange}
            error={formik.touched.code && Boolean(formik.errors.code)}
            helperText={formik.touched.code && formik.errors.code}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {formik.values.preCode}
                </InputAdornment>
              ),
            }}
          />
          <TextFieldTheme
            className="text-name"
            label={`${t("ItemName")}`}
            variant={"outlined"}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <FormControl
            component="fieldset"
            className="radio"
            value={formik.values.isTaxable}
            error={formik.touched.isTaxable && Boolean(formik.errors.isTaxable)}
          >
            <FormLabel component="legend">{`${t("TaxCalculate")} ?`}</FormLabel>
            <RadioGroup
              row
              aria-label="isTaxable"
              onChange={formik.handleChange}
              name="isTaxable"
              value={formik.values.isTaxable}
            >
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={t("Yes")}
              />
              <FormControlLabel value={0} control={<Radio />} label={t("No")} />
            </RadioGroup>
            {formik.touched.isTaxable && Boolean(formik.errors.isTaxable) && (
              <FormHelperText>{formik.errors.isTaxable}</FormHelperText>
            )}
          </FormControl>
          <FormControl
            component="fieldset"
            className="radio"
            value={formik.values.isSso}
            error={formik.touched.isSso && Boolean(formik.errors.isSso)}
          >
            <FormLabel component="legend">{`${t(
              "SocialSecurityCalculate"
            )} ?`}</FormLabel>
            <RadioGroup
              row
              aria-label="isSso"
              onChange={formik.handleChange}
              name="isSso"
              value={formik.values.isSso}
            >
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={t("Yes")}
              />
              <FormControlLabel value={0} control={<Radio />} label={t("No")} />
            </RadioGroup>
            {formik.touched.isSso && Boolean(formik.errors.isSso) && (
              <FormHelperText>{formik.errors.isSso}</FormHelperText>
            )}
          </FormControl>
          <FormControl
            component="fieldset"
            className="radio"
            value={formik.values.isPf}
            error={formik.touched.isPf && Boolean(formik.errors.isPf)}
          >
            <FormLabel component="legend">{`${t(
              "ProvidentFundCalculate"
            )} ?`}</FormLabel>
            <RadioGroup
              row
              aria-label="isPf"
              onChange={formik.handleChange}
              name="isPf"
              value={formik.values.isPf}
            >
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={t("Yes")}
              />
              <FormControlLabel value={0} control={<Radio />} label={t("No")} />
            </RadioGroup>
            {formik.touched.isPf && Boolean(formik.errors.isPf) && (
              <FormHelperText>{formik.errors.isPf}</FormHelperText>
            )}
          </FormControl>
          <FormControl
            component="fieldset"
            className="radio"
            value={formik.values.isEWF}
            error={formik.touched.isEWF && Boolean(formik.errors.isEWF)}
          >
            <FormLabel component="legend">{`${t(
              "EmployeeWelfareFundCalculate"
            )} ?`}</FormLabel>
            <RadioGroup
              row
              aria-label="isEWF"
              onChange={formik.handleChange}
              name="isEWF"
              value={formik.values.isEWF}
            >
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={t("Yes")}
              />
              <FormControlLabel value={0} control={<Radio />} label={t("No")} />
            </RadioGroup>
            {formik.touched.isEWF && Boolean(formik.errors.isEWF) && (
              <FormHelperText>{formik.errors.isEWF}</FormHelperText>
            )}
          </FormControl>
          <FormControl
            component="fieldset"
            className="radio"
            value={formik.values.isPercentage}
            error={
              formik.touched.isPercentage && Boolean(formik.errors.isPercentage)
            }
          >
            <FormLabel component="legend">
              {t("AmountIncreaseDecrease")}
            </FormLabel>
            <RadioGroup
              row
              aria-label="isPercentage"
              onChange={formik.handleChange}
              name="isPercentage"
              value={formik.values.isPercentage}
            >
              <FormControlLabel
                value={0}
                control={<Radio />}
                label={t("SpecifyNumber")}
              />
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={t("PercentageOfSalary")}
              />
            </RadioGroup>
            {formik.touched.isPercentage &&
              Boolean(formik.errors.isPercentage) && (
                <FormHelperText>{formik.errors.isPercentage}</FormHelperText>
              )}
          </FormControl>

          <FormControl
            component="fieldset"
            className="radio"
            error={formik.touched.mode && Boolean(formik.errors.mode)}
          >
            <FormLabel component="legend">{t("BillingChargeCalculation")}</FormLabel>
            <RadioGroup
              row
              aria-label="mode"
              onChange={formik.handleChange}
              name="mode"
              value={formik.values.mode}
            >
              <FormControlLabel
                value="1"
                control={<Radio />}
                label={t("Normal")}
              />
              <FormControlLabel
                value="2"
                control={<Radio />}
                label={t("ChargeAmount")}
              />
              <FormControlLabel
                value="3"
                control={<Radio />}
                label={t("CalculatedAsAPercentage")}
              />
            </RadioGroup>
            {formik.touched.mode && Boolean(formik.errors.mode) && (
              <FormHelperText>{formik.errors.mode}</FormHelperText>
            )}
          </FormControl>

          {(parseInt(formik.values.mode) === 2 ) && (
            <TextFieldTheme
              className="text-name"
              label={t("Amount")}
              variant={"outlined"}
              name="amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
              sx={{ maxWidth: 200 }}
            />
          )}

          {(parseInt(formik.values.mode) === 3) && (
            <TextFieldTheme
              className="text-name"
              label={t("Amount")}
              variant={"outlined"}
              name="percent"
              value={formik.values.percent}
              onChange={formik.handleChange}
              error={formik.touched.percent && Boolean(formik.errors.percent)}
              helperText={formik.touched.percent && formik.errors.percent}
              sx={{ maxWidth: 200 }}
            />
          )}


          <StyledFooter>
            <ButtonBlue className="cancel" onClick={handleClose}>
              {t("Cancel")}
            </ButtonBlue>
            <ButtonBlue type="submit" variant="contained">
              {t("Save")}
            </ButtonBlue>
          </StyledFooter>
        </form>
      </StyledContent>
    </DrawerCustom>
  );
};

export default DialogNew;
